import './styles.css';
import { Game } from './engine/game';
import type { GameEvent } from './engine/game';
import { Display } from './render/display';
import { Sound } from './audio/sound';
import { InputController, type Button, type InputHandler } from './ui/input';
import { OptionsScreen } from './ui/options';
import {
  loadOptions,
  saveOptions,
  loadHighscores,
  submitHighscore,
  type Options,
} from './storage/storage';

function must<T extends Element>(id: string): T {
  const el = document.getElementById(id);
  if (!el) throw new Error(`Element #${id} fehlt`);
  return el as unknown as T;
}

const els = {
  screen: must<HTMLElement>('screen'),
  playfield: must<HTMLElement>('playfield'),
  nextGrid: must<HTMLElement>('next-grid'),
  score: must<HTMLElement>('val-score'),
  level: must<HTMLElement>('val-level'),
  lines: must<HTMLElement>('val-lines'),
  overlay: must<HTMLElement>('overlay'),
  overlayTitle: must<HTMLElement>('overlay-title'),
  overlayText: must<HTMLElement>('overlay-text'),
  options: must<HTMLElement>('options'),
};

// ---- Zustand & Module ----
let options: Options = loadOptions();

const game = new Game({ startLevel: options.startLevel });
const sound = new Sound(!options.sound);
const display = new Display(
  els.playfield,
  els.nextGrid,
  els.score,
  els.level,
  els.lines,
  game.cols,
  game.rows,
);

const optionsScreen = new OptionsScreen(
  els.options,
  options,
  (o) => applyOptions(o),
  () => loadHighscores(),
);

function applyOptions(o: Options): void {
  options = o;
  saveOptions(o);
  game.setStartLevel(o.startLevel);
  sound.setMuted(!o.sound);
  render();
}

// ---- Spielereignisse: Sound, Vibration, Highscore, Wake Lock ----
game.on((e: GameEvent) => {
  sound.handle(e);
  if (e.type === 'lock' || e.type === 'lines') vibrate(10);
  if (e.type === 'start') requestWakeLock();
  if (e.type === 'gameover') {
    releaseWakeLock();
    submitHighscore({
      score: game.score,
      lines: game.lines,
      level: game.level,
      date: new Date().toISOString(),
    });
  }
});

// ---- Eingabe-Handler (Dispatch je Zustand) ----
const handler: InputHandler = {
  isInteractive: () => game.state === 'playing' && !optionsScreen.isOpen,
  unlock: () => sound.unlock(),
  selectLong: () => {
    if (!optionsScreen.isOpen && game.state === 'playing') game.pause();
    optionsScreen.toggle();
    render();
  },
  press: (btn: Button) => {
    if (optionsScreen.isOpen) {
      optionsScreen.handle(btn);
      render();
      return;
    }
    const st = game.state;
    switch (btn) {
      case 'left':
        if (st === 'playing') game.moveLeft();
        break;
      case 'right':
        if (st === 'playing') game.moveRight();
        break;
      case 'up':
      case 'b':
        if (st === 'playing') game.rotateCW();
        break;
      case 'down':
        if (st === 'playing') game.softDrop();
        break;
      case 'a':
        if (st === 'playing') game.hardDrop();
        break;
      case 'start':
        game.startButton();
        break;
      case 'select':
        // SELECT (kurz): Neustart, wenn nicht im Spiel.
        if (st !== 'playing') game.newGame();
        break;
    }
    render();
  },
};

new InputController(document, els.screen, handler);

// ---- Rendering ----
function render(): void {
  display.render(game, options.ghost);
  updateOverlay();
}

function updateOverlay(): void {
  if (optionsScreen.isOpen || game.state === 'playing') {
    els.overlay.hidden = true;
    return;
  }
  els.overlay.hidden = false;
  if (game.state === 'ready') {
    els.overlayTitle.textContent = '#ITZEpoche';
    els.overlayText.textContent = 'BLOCKS\n\nSTART DRÜCKEN';
  } else if (game.state === 'paused') {
    els.overlayTitle.textContent = 'PAUSE';
    els.overlayText.textContent = 'START = WEITER';
  } else {
    els.overlayTitle.textContent = 'GAME OVER';
    els.overlayText.textContent = `SCORE ${game.score}\nSTART = NEU`;
  }
}

// ---- Feste Tick-Rate über rAF + Akkumulator ----
let last = performance.now();
function frame(now: number): void {
  let dt = now - last;
  last = now;
  if (dt > 100) dt = 100; // Hintergrund-Tabs / Sprünge begrenzen
  game.update(dt);
  render();
  requestAnimationFrame(frame);
}

// ---- Wake Lock (best effort) ----
interface WakeLockSentinelLike {
  release: () => Promise<void>;
}
interface WakeLockLike {
  request: (type: 'screen') => Promise<WakeLockSentinelLike>;
}
let wakeLock: WakeLockSentinelLike | null = null;

function requestWakeLock(): void {
  const wl = (navigator as unknown as { wakeLock?: WakeLockLike }).wakeLock;
  if (!wl) return;
  wl.request('screen')
    .then((s) => {
      wakeLock = s;
    })
    .catch(() => {
      /* verweigert — egal */
    });
}
function releaseWakeLock(): void {
  wakeLock?.release().catch(() => undefined);
  wakeLock = null;
}
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible' && game.state === 'playing') requestWakeLock();
});

function vibrate(ms: number): void {
  navigator.vibrate?.(ms);
}

// ---- Service Worker (PWA) ----
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    const url = `${import.meta.env.BASE_URL}sw.js`;
    navigator.serviceWorker.register(url).catch(() => {
      /* offline nicht verfügbar — egal */
    });
  });
}

// ---- Start ----
render();
requestAnimationFrame(frame);
